import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Modal, Form, Table, Typography, Flex, Card, Alert, Tooltip, Button, Input, Select } from "antd"
import { ResponsivePage } from "ui/ResponsivePage"
import { Grid } from "antd"
import { StarOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import ExperienceSelector from "../experience/ExperienceSelector"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import EquipmentListingMedia from "../UploadEquipmentPhoto"
import { Text } from "ui/Typography"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import AssetListDetails from "../AssetlistDetails/AssetlistDetails"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { AlertIcon } from "ui/Icon/DSIcon/AlertIcon"
import { spacings } from "themes/variables"
import { getTrips, getGuideCategoryToBoats, getGuideCharacteristicToBoats } from "store/boats"
import { useEquipmentCharacteristics } from "./hooks/useEquipmentCharacteristics"
import { useEquipmentForm } from "./hooks/useEquipmentForm"
import { useEquipmentSubmit } from "./hooks/useEquipmentSubmit"
import { useCategoryManagement } from "./hooks/useCategoryManagement"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"
import CategorySelect from "./CategorySelect"
import ExperienceMobileList from "./ExperienceMobileList"
import { buildCharacteristicIdMap, buildRecordCharacteristicMap } from "./helpers"
import { sanitizeMultiWordTyping, sanitizeMultiWord } from "utils/InputSanitizers"
import styles from "./index.module.css"
import { getExperienceTableColumns, getModalConfig } from "./constants.js"

const { Title } = Typography

const AddEquipmentModal = ({ visible, onCancel, boatId, record, boatLoading, isDuplicateMode = false, onSubmit }) => {
  const [isDirty, setIsDirty] = useState(false)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [isInitializing, setIsInitializing] = useState(true)
  const { t } = useTranslation()

  // Manage category creation/search
  const categoryManagement = useCategoryManagement()

  useEffect(() => {
    if (visible) {
      setIsDirty(false)
      setIsInitializing(true)
    }
  }, [visible])

  const handleRequestClose = () => {
    if (isDirty) {
      setShowUnsavedModal(true)
    } else {
      onCancel()
    }
  }

  const handleDiscard = () => {
    setShowUnsavedModal(false)
    setIsDirty(false)
    onCancel()
  }
  const dispatch = useDispatch()

  const categoryListing = useSelector((state) => state.boats.category_to_boats.items)
  const categoryOptions = categoryListing?.map((cat) => ({ label: cat.name, value: String(cat.id) }))
  const characteristicListing = useSelector((state) => state.boats.characteristic_to_boats.items) || []

  // Build characteristic ID maps using helper functions
  const recordCharacteristicIdToLabel = React.useMemo(() => buildRecordCharacteristicMap(record), [record?.characteristics])

  const characteristicIdToLabel = React.useMemo(
    () => buildCharacteristicIdMap(characteristicListing, recordCharacteristicIdToLabel),
    [characteristicListing, recordCharacteristicIdToLabel]
  )

  // Initialize characteristics hook
  const characteristicsHook = useEquipmentCharacteristics(characteristicListing, characteristicIdToLabel)

  // Initialize form hook
  const formHook = useEquipmentForm(record, boatLoading, boatId, isDuplicateMode, () => categoryName, characteristicsHook)

  // Get category info (after form is created)
  const categoryId = Form.useWatch("category", formHook.form)
  const categoryName = categoryOptions?.find((cat) => cat.value === String(categoryId))?.label || ""

  // Initialize submit hook
  const submitHook = useEquipmentSubmit(formHook, characteristicsHook, record, isDuplicateMode, onCancel, onSubmit)

  // Guide category to boats state
  useEffect(() => {
    dispatch(getGuideCategoryToBoats())
    dispatch(getTrips())
    dispatch(getGuideCharacteristicToBoats())
  }, [dispatch])

  useEffect(() => {
    if ((visible && record?.loaded && !boatLoading) || (visible && !boatId)) {
      const timer = setTimeout(() => {
        setIsInitializing(false)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [visible, record?.version, boatLoading, record?.loaded, boatId])

  const columns = getExperienceTableColumns(formHook.handleRemoveExperience, setIsDirty)

  const modalConfig = getModalConfig(isDuplicateMode, !!record?.id, t)
  const { title: modalTitle, submitButtonText } = modalConfig

  const { useBreakpoint } = Grid
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const tableData = formHook.selectedExperienceData

  const modalContent = (
    <FirstLoading loading={boatLoading}>
      <Form
        initialValues={{ name: "", category: undefined, max_users: 1, description: "" }}
        form={formHook.form}
        onFinish={submitHook.handleSubmit}
        onValuesChange={() => {
          if (!isInitializing) {
            setIsDirty(true)
          }
        }}
        layout="vertical"
        className={styles.equipmentForm}
      >
        <Flex vertical gap={spacings.S} className={styles.sectionsWrapper}>
          {!isDuplicateMode && (
            <Alert
              className={styles.infoText}
              showIcon
              icon={<AlertIcon />}
              type="info"
              description="Saving changes will affect all listings that use this pool."
            />
          )}

          <Card className={styles.assignExperiencesSection}>
            <Text size="lg" variant="heading" fw="600" className={styles.sectionTitle}>
              {t("equipment.equipment_photo")}
            </Text>
            <Form.Item label={t("boat.images")} className={`${styles.equipmentListingMedia} mt-4 mb-0`}>
              <EquipmentListingMedia
                filteredMedia={formHook.filteredMedia}
                setFilteredMedia={(val) => {
                  setIsDirty(true)
                  formHook.setFilteredMedia(val)
                }}
                localMedia={formHook.localMedia}
                setLocalMedia={(val) => {
                  setIsDirty(true)
                  formHook.setLocalMedia(val)
                }}
              />
            </Form.Item>
          </Card>

          <Card className={styles.assignExperiencesSection}>
            <Title level={5} className={`${styles.sectionTitle} mb-4`}>
              {t("equipment.equipment_information")}
            </Title>
            <LabeledField
              name="name"
              labelKey={t("equipment.title")}
              rules={[
                {
                  required: true,
                  message: t("equipment.title_required")
                }
              ]}
            >
              <Input
                placeholder={t("equipment.title_placeholder")}
                onChange={(e) => {
                  e.target.value = sanitizeMultiWordTyping(e.target.value)
                }}
                onBlur={(e) => {
                  formHook.form.setFieldValue("name", sanitizeMultiWord(e.target.value))
                }}
                onPaste={(e) => {
                  e.preventDefault()
                  const text = sanitizeMultiWord(e.clipboardData.getData("text"))
                  formHook.form.setFieldValue("name", text)
                }}
              />
            </LabeledField>
            <CategorySelect
              categoryOptions={categoryOptions}
              selectedCategoryId={categoryId}
              categoryManagement={categoryManagement}
              onCategoryChange={() => {}}
              form={formHook.form}
            />

            <LabeledField
              name="max_users"
              labelKey={t("equipment.max_users")}
              rules={[
                {
                  required: true,
                  message: t("equipment.max_users_required")
                }
              ]}
            >
              <Input
                type="number"
                min={1}
                className="w-100 input-users"
                onInput={(e) => {
                  const value = e.target.value
                  if (value === "" || Number.isNaN(value)) return
                  const intValue = Math.max(1, Number.parseInt(value, 10) || 1)
                  e.target.value = intValue
                }}
              />
            </LabeledField>
            <Flex justify="space-between" align="center" className="mb-xxs" gap={spacings.XS}>
              <Text fw="500">{t("equipment.description")}</Text>
              <Tooltip title={formHook.aiDescriptionData?.length < 25 ? t("marketing.ai_enable_text_generation") : ""} placement="top">
                <span>
                  <Button
                    type="primary"
                    icon={<StarOutlined />}
                    onClick={() => formHook.setShowAiModal(true)}
                    disabled={!formHook.aiDescriptionData || formHook.aiDescriptionData.length < 25}
                    style={!formHook.aiDescriptionData || formHook.aiDescriptionData.length < 25 ? { pointerEvents: "not-allowed" } : {}}
                  >
                    {t("pro.ai_enhancement.button_text")}
                  </Button>
                </span>
              </Tooltip>
            </Flex>
            <LabeledField
              name="description"
              noStyle
              rules={[
                {
                  required: true,
                  message: t("equipment.description_required")
                }
              ]}
            >
              <Input.TextArea
                rows={5}
                placeholder={t("equipment.description_placeholder")}
                value={formHook.aiDescriptionData}
                onChange={(e) => {
                  const value = e.target.value
                  formHook.setAiDescriptionData(value)
                  formHook.form.setFieldsValue({ description: value })
                }}
              />
            </LabeledField>
            {formHook.showAiModal && (
              <AiEnhancementModal
                className="ai-enhancement-modal"
                open={formHook.showAiModal}
                description={formHook.aiDescriptionData}
                setDescription={(desc) => {
                  formHook.setAiDescriptionData(desc)
                  formHook.form.setFieldsValue({ description: desc })
                }}
                profileDescription={formHook.profileDescription}
                setProfileDescription={formHook.setProfileDescription}
                onOk={() => {
                  formHook.setShowAiModal(false)
                }}
                onCancel={() => {
                  formHook.setShowAiModal(false)
                  formHook.setProfileDescription("")
                }}
                text="Equipment Description"
                modalTitle={t("pro.ai_enhancement.modal_title")}
                messages={t("pro.ai_enhancement.form.continue_refinement_info")}
                refinementTooltipTitle={t("pro.ai_enhancement.refinement_tooltip")}
                improveWithAITooltipTitle={t("pro.ai_enhancement.improve_with_ai_tooltip")}
              />
            )}

            <div className="characteristics-section my-6 mb-0">
              <LabeledField name="characteristics" labelKey={t("equipment.add_characteristics")}>
                <Select
                  mode="tags"
                  showSearch
                  allowClear
                  placeholder="Select characteristics"
                  value={characteristicsHook.selectedIds}
                  onChange={(ids, options) => characteristicsHook.handleCharacteristicsChange(ids, options, formHook.form)}
                  className={styles.characteristicsSelect}
                  options={characteristicsHook.characteristicOptions}
                  optionLabelProp="label"
                  optionFilterProp="label"
                  tagRender={characteristicsHook.renderCharacteristicTag}
                  filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())}
                />
              </LabeledField>
            </div>
          </Card>

          <Card className={styles.assignExperiencesSection}>
            <Title level={5} className={`${styles.sectionTitle} mb-5`}>
              {t("equipment.modals.assign_experiences")}{" "}
              <span className={styles.experiencesCount}>({formHook.selectedExperienceData.length})</span>
            </Title>
            <LabeledField name="experiences" labelKey={t("equipment.experiences")} className="mb-0">
              <ExperienceSelector
                experiences={formHook.experiencesMemo}
                value={formHook.selectedExperiences}
                onChange={(vals) => {
                  const strVals = vals.map(String)
                  // Avoid redundant updates: only set if actually changed
                  if (
                    strVals.length !== formHook.selectedExperiences.length ||
                    strVals.some((v, i) => v !== formHook.selectedExperiences[i])
                  ) {
                    if (!isInitializing) {
                      setIsDirty(true)
                    }
                    formHook.setSelectedExperiences(strVals)
                    formHook.form.setFieldsValue({ experiences: strVals })
                  }
                }}
              />
              {formHook.selectedExperienceData.length === 0 && (
                <Text size="caption" className={styles.emptyExperiencesText}>
                  {t("equipment.no_experience_assigned", { defaultValue: "No experience assigned to this equipment." })}
                </Text>
              )}
              {tableData.length > 0 && (
                <>
                  {isMobile ? (
                    <ExperienceMobileList
                      experiences={tableData}
                      onRemoveExperience={formHook.handleRemoveExperience}
                      setIsDirty={setIsDirty}
                    />
                  ) : (
                    <Table
                      dataSource={formHook.selectedExperienceData}
                      rowKey="id"
                      pagination={false}
                      columns={columns}
                      scroll={{ y: 400 }}
                    />
                  )}
                </>
              )}
            </LabeledField>
          </Card>

          <Card className={styles.assignExperiencesSection}>
            <AssetListDetails
              form={formHook.form}
              categoryName={categoryName}
              assets={formHook.assets}
              setAssets={formHook.setAssets}
              equipmentId={record?.id}
            />
          </Card>
        </Flex>
        <Flex gap={spacings.S} className={styles.footerActions}>
          <Button variant="outlined" onClick={handleRequestClose} color="primary" className={styles.footerCancelBtn}>
            {t("common.cancel")}
          </Button>
          <Button variant="solid" htmlType="submit" color="primary" loading={submitHook.isLoading} className={styles.footerSaveBtn}>
            {submitButtonText}
          </Button>
        </Flex>
      </Form>
    </FirstLoading>
  )

  return isMobile ? (
    <>
      <ResponsivePage onBackClick={onCancel} pageTitle={modalTitle}>
        {modalContent}
      </ResponsivePage>
      <UnsavedChangesModal open={showUnsavedModal} onDiscard={handleDiscard} onKeepEditing={() => setShowUnsavedModal(false)} />
    </>
  ) : (
    <>
      <Modal
        title={modalTitle}
        open={visible}
        onCancel={handleRequestClose}
        className={styles.addEquipmentModal}
        width={800}
        footer={null}
        maskClosable={false}
      >
        {modalContent}
      </Modal>
      <UnsavedChangesModal open={showUnsavedModal} onDiscard={handleDiscard} onKeepEditing={() => setShowUnsavedModal(false)} />
    </>
  )
}

AddEquipmentModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  record: PropTypes.object,
  boatId: PropTypes.any,
  boatLoading: PropTypes.bool,
  isDuplicateMode: PropTypes.bool,
  onSubmit: PropTypes.func
}

export default AddEquipmentModal
